import React, { useState, useEffect, lazy, Suspense, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import ProfileCard from "@/components/play/ProfileCard";
import QuickLinks from "@/components/play/QuickLinks";
import BrandChallenges from "@/components/play/BrandChallenges";
import CommunityCards from "@/components/play/CommunityCards";
import GameSelector from "@/components/play/GameSelector";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import GoVipRewardsInventoryDrawer from "@/components/goVip/GoVipRewardsInventoryDrawer";
import OverwolfGoVipAccountLevel from "@/pages/overwolf/components/OverwolfGoVipAccountLevel";
import { usePlayPageData, usePlayPageState } from "@/hooks/usePlayPageData";
import { useGetActiveChallenges } from "@/hooks/challenges/useGetActiveChallenges";
import useGetGuildOwnerships from "@/hooks/useGetGuildOwnerships";
import { useGetOverwolfSummary } from "@/hooks/useGetOverwolfSummary";
import { PLAY_PAGE_TABS, PLAY_PAGE_FEATURES } from "@/config/playPageConfig";
import type { TabComponentProps, FormattedChallenge } from "@/types/play";
import "./PlayEnhanced.scss";

// Constants
const MAX_CHALLENGES_DISPLAY = 5;
const CHALLENGE_PLACEHOLDER_IMAGE = "https://cdn.devourgo.io/images/brand-challenge-placeholder.png";

// Lazy load tab components for better performance
const GamesTab = lazy(() => import("@/components/play/tabs/GamesTab"));
const GuildsTab = lazy(() => import("@/components/play/tabs/GuildsTab"));
const SharePlayTab = lazy(() => import("@/components/play/tabs/SharePlayTab"));
const InventoryTab = lazy(() => import("@/components/play/tabs/InventoryTab"));

// Tab components mapping with proper types
const TAB_COMPONENTS: Record<string, React.LazyExoticComponent<React.FC<TabComponentProps>>> = {
    games: GamesTab,
    guilds: GuildsTab,
    share: SharePlayTab,
    inventory: InventoryTab,
};

/**
 * Enhanced Command Header Component
 * Displays user stats in a cockpit-style header
 */
const CommandHeader: React.FC<{
    fuelBalance: string;
    isLoading: boolean;
}> = ({ fuelBalance, isLoading }) => {
    const accountLevel = useSelector((store: IStore) => store.metaStore.accountLevel);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const { data: overwolfSummary } = useGetOverwolfSummary();

    // Calculate XP percentage using actual data structure
    const experienceGained = accountLevel?.experienceGainedAtThisLevel || 0;
    const experienceRequired = accountLevel?.experienceRequiredForNextLevel || 1;
    const totalLevelExperience = experienceGained + experienceRequired;
    const xpPercentage = experienceGained / totalLevelExperience * 100;

    return (
        <header className="command-header">
            <div className="command-header__status">
                <span className="command-header__status-dot"></span>
                <span className="command-header__status-text">COMMAND CENTER ONLINE</span>
            </div>

            <div className="command-header__stats">
                {/* Level Badge */}
                <div className="command-header__level">
                    <div className="command-header__level-badge">
                        {accountLevel?.level || 1}
                    </div>
                    <div className="command-header__xp">
                        <span className="command-header__xp-label">EXPERIENCE</span>
                        <div className="command-header__xp-bar">
                            <div
                                className="command-header__xp-fill"
                                style={{ width: `${xpPercentage}%` }}
                            />
                        </div>
                        <span className="command-header__xp-text">
                            {experienceGained.toLocaleString()} / {totalLevelExperience.toLocaleString()}
                        </span>
                    </div>
                </div>

                {/* Fuel */}
                <div className="command-header__stat">
                    <span className="command-header__stat-value">{fuelBalance}</span>
                    <span className="command-header__stat-label">FUEL</span>
                </div>

                {/* Game Status - Placeholder for Phase 2 */}
                {overwolfSummary?.currentGame &&
          <div className="command-header__game-status">
              <span className="command-header__game-icon">🎮</span>
              <span>{overwolfSummary.currentGame.name || "In Game"}</span>
          </div>
                }
            </div>

            <div className="command-header__actions">
                <button className="command-header__icon-btn">🔔</button>
                <button className="command-header__icon-btn">
                    {currentUser?.user?.gravatar
                        ? <img
                            src={currentUser.user.gravatar}
                            alt="Profile"
                            className="command-header__avatar"
                        />
                        : <span>👤</span>
                    }
                </button>
            </div>
        </header>
    );
};

/**
 * Guild Info Panel Component
 * Left sidebar with guild information
 */
const GuildInfoPanel: React.FC = () => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { data: guildOwnerships, isLoading } = useGetGuildOwnerships(fullToken);
    const navigate = useNavigate();

    // TODO: Need to check actual data structure of guildOwnerships
    const activeGuild = guildOwnerships?.guilds?.[0];

    if (isLoading) {
        return (
            <div className="guild-panel guild-panel--loading">
                <div className="skeleton-line skeleton-line--thick" />
                <div className="skeleton-line skeleton-line--thin" />
                <div className="skeleton-line skeleton-line--medium" />
            </div>
        );
    }

    if (!activeGuild) {
        return (
            <div className="guild-panel guild-panel--empty">
                <h3>Join a Guild</h3>
                <p>Connect with other players and earn rewards together!</p>
                <button
                    className="guild-panel__cta"
                    onClick={() => navigate("/guilds")}
                >
                    Browse Guilds
                </button>
            </div>
        );
    }

    return (
        <div className="guild-panel">
            <div className="guild-panel__header">
                <span className="guild-panel__title">GUILD HQ</span>
            </div>

            {/* Guild Badge - Placeholder until we know data structure */}
            <div className="guild-panel__badge">
                {activeGuild.abbreviation || activeGuild.tag || "GUILD"}
            </div>

            <h3 className="guild-panel__name">{activeGuild.name}</h3>

            {/* Guild Stats - Need actual data structure */}
            <div className="guild-panel__placeholder">
                <p className="guild-panel__placeholder-text">
                    Guild stats will appear here once we verify the data structure
                </p>
                {/* Placeholder for:
          - Guild rank
          - Member count
          - Your rank in guild
          - Guild XP/progress
        */}
            </div>

            <button
                className="guild-panel__chat-btn"
                onClick={() => {
                    // TODO: Navigate to guild chat or open Discord
                    console.log("Guild chat clicked");
                }}
            >
                💬 Guild Chat
            </button>
        </div>
    );
};

/**
 * Activity Feed Component
 * Right sidebar with live updates
 */
const ActivityFeed: React.FC = () => {
    // This would connect to a real-time feed in Phase 2+
    const mockFeedItems = [
        { id: 1, icon: "🏆", text: "xXSniperXx unlocked Sharpshooter badge", time: "4 min ago" },
        { id: 2, icon: "✓", text: "Completed ACE MACHINE quest", time: "15 min ago" },
        { id: 3, icon: "👥", text: "Guild ranked up to #6", time: "1 hour ago" },
        { id: 4, icon: "💰", text: "Received 500 FUEL reward", time: "2 hours ago" },
    ];

    return (
        <div className="activity-feed">
            <div className="activity-feed__header">
                <div className="activity-feed__title">
                    <span className="activity-feed__live-dot"></span>
                    <span>LIVE FEED</span>
                </div>
                <button className="activity-feed__filter">ALL ▼</button>
            </div>

            <div className="activity-feed__list">
                {mockFeedItems.map(item =>
                    <div key={item.id} className="activity-feed__item">
                        <div className="activity-feed__icon">{item.icon}</div>
                        <div className="activity-feed__content">
                            <div dangerouslySetInnerHTML={{ __html: item.text }} />
                            <div className="activity-feed__time">{item.time}</div>
                        </div>
                    </div>)}
            </div>
        </div>
    );
};

/**
 * Enhanced Play Page Component
 * Cockpit-style gaming command center
 */
const PlayEnhanced: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { savedTab, savedGameId } = usePlayPageState();
    const [selectedGameId, setSelectedGameId] = useState<string | null>(savedGameId);

    // Get consolidated data from custom hook
    const {
        fullToken,
        fuelBalance,
        isLoading,
        hasError,
        errors,
    } = usePlayPageData();

    // Fetch active challenges for Brand Challenges section
    const { data: challengesData, isLoading: challengesLoading, error: challengesError } = useGetActiveChallenges({
        limit: MAX_CHALLENGES_DISPLAY,
    });

    /**
     * Transform challenges data to match BrandChallenges component format
     */
    const formattedChallenges = useMemo<FormattedChallenge[]>(() => {
        if (!challengesData?.challenges) return [];

        return (challengesData.challenges || [])
            .filter(challenge => challenge.isActive === true)
            .map(challenge => ({
                id: challenge.id,
                name: challenge.name,
                cardTitle: challenge.cardTitle,
                description: challenge.description || "",
                brandLogo: challenge.brandLogo,
                brandColor: challenge.brandColor || "#aa96ef",
                brandMapSlug: challenge.brandMapSlug,
                backgroundImageLarge: challenge.backgroundImageLarge || challenge.imageUrl,
                backgroundImageSmall: challenge.backgroundImageSmall || challenge.imageUrl,
                start: challenge.start || 0,
                end: challenge.end || 0,
                isAccepted: challenge.isAccepted,
                imageUrl: challenge.imageUrl || CHALLENGE_PLACEHOLDER_IMAGE,
                active: true,
            }));
    }, [challengesData]);

    // Initialize active tab from URL or saved state
    const urlTab = searchParams.get("tab");
    const [activeTab, setActiveTab] = useState(urlTab || savedTab || "games");

    // State for rewards drawer
    const [showRewardsDrawer, setShowRewardsDrawer] = useState(false);

    /**
     * Update URL and localStorage when tab changes
     */
    useEffect(() => {
        if (PLAY_PAGE_FEATURES.tabs.deepLinking) {
            setSearchParams({ tab: activeTab });
        }

        if (PLAY_PAGE_FEATURES.tabs.persistSelection) {
            try {
                localStorage.setItem("play-active-tab", activeTab);
            } catch (error) {
                console.warn("Failed to save tab preference to localStorage:", error);
            }
        }
    }, [activeTab, setSearchParams]);

    /**
     * Handle tab change with proper event handling
     */
    const handleTabChange = useCallback((tabId: string, event?: React.MouseEvent) => {
        if (event) {
            event.preventDefault();
        }
        setActiveTab(tabId);
    }, []);

    // Get the active tab component
    const ActiveTabComponent = TAB_COMPONENTS[activeTab] || TAB_COMPONENTS.games;
    const enabledTabs = PLAY_PAGE_TABS.filter(tab => tab.enabled);

    return (
        <div className="play play--cockpit">
            {/* Enhanced Command Header */}
            <CommandHeader
                fuelBalance={fuelBalance}
                isLoading={isLoading}
            />

            {/* Main Cockpit Grid */}
            <div className="cockpit-container">
                {/* Left Column - Guild HQ */}
                <aside className="cockpit-container__left">
                    <GuildInfoPanel />

                    {/* Quick Actions */}
                    <QuickLinks
                        isLoading={isLoading}
                        onRewardsClick={() => setShowRewardsDrawer(true)}
                    />
                </aside>

                {/* Center Column - Battle Station */}
                <main className="cockpit-container__center">
                    {/* Game Selector - Placeholder for Phase 1 */}
                    <div className="game-selector-panel">
                        <div className="game-selector-panel__header">
                            <span className="game-selector-panel__title">SELECT BATTLEGROUND</span>
                        </div>
                        <div className="game-selector-placeholder">
                            <div className="game-selector-placeholder__coming-soon">COMING SOON</div>
                            <p className="game-selector-placeholder__text">Game selection will be available in Phase 2</p>
                        </div>
                    </div>

                    {/* Tab Navigation with enhanced styling */}
                    <div className="quest-panel">
                        <div className="quest-panel__tabs" role="tablist" aria-label="Quest periods">
                            {enabledTabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        className={`quest-panel__tab ${activeTab === tab.id ? "quest-panel__tab--active" : ""}`}
                                        onClick={(e) => handleTabChange(tab.id, e)}
                                        type="button"
                                        role="tab"
                                        aria-label={`Switch to ${tab.label} tab`}
                                        aria-selected={activeTab === tab.id}
                                        aria-controls={`tabpanel-${tab.id}`}
                                        tabIndex={activeTab === tab.id ? 0 : -1}
                                    >
                                        <Icon className="quest-panel__tab-icon" aria-hidden="true" />
                                        <span className="quest-panel__tab-label">{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Tab Content */}
                        <div
                            className="quest-panel__content"
                            role="tabpanel"
                            id={`tabpanel-${activeTab}`}
                            aria-labelledby={activeTab}
                        >
                            <ErrorBoundary>
                                <Suspense fallback={
                                    <div className="quest-panel__loading">
                                        <div className="quest-panel__spinner" />
                                        <p>Preparing battleground...</p>
                                    </div>
                                }>
                                    <ActiveTabComponent initialGameId={savedGameId} />
                                </Suspense>
                            </ErrorBoundary>
                        </div>
                    </div>
                </main>

                {/* Right Column - Intel Feed */}
                <aside className="cockpit-container__right">
                    {/* Brand Drop - Featured Challenge */}
                    {formattedChallenges.length > 0 &&
            <div className="brand-drop">
                <span className="brand-drop__label">🔥 BRAND DROP</span>
                <BrandChallenges
                    challenges={formattedChallenges}
                    isLoading={isLoading || challengesLoading}
                />
            </div>
                    }

                    {/* Activity Feed */}
                    <ActivityFeed />

                    {/* Community Cards (optional) */}
                    <div className="cockpit-container__community">
                        <CommunityCards isLoading={isLoading} />
                    </div>
                </aside>
            </div>

            {/* Rewards Drawer */}
            <GoVipRewardsInventoryDrawer
                show={showRewardsDrawer}
                toggle={() => setShowRewardsDrawer(false)}
                preselectedNftId={undefined}
            />
        </div>
    );
};

export default PlayEnhanced;